import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ConfirmationModal } from './Helpers';
import { fetchDuesDefinitions } from '../api/duesDefinitionApi';
import { fetchApartments } from '../api/apartmentApi'; // Daireleri çekmek için

const CommonExpensesView = ({ showToast, currentUser }) => {
  const [duesDefinitions, setDuesDefinitions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDuesDefinition, setEditingDuesDefinition] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [duesDefinitionToDelete, setDuesDefinitionToDelete] = useState(null);
  const [apartments, setApartments] = useState([]); // Yeni: Daire listesi
  const [totalAmount, setTotalAmount] = useState(''); // Yeni: Toplam tutar
  const [distributionMethod, setDistributionMethod] = useState('arsaPayi'); // Yeni: Dağıtım yöntemi
  const [exemptApartmentIds, setExemptApartmentIds] = useState([]); // Yeni: Muaf daireler

  const loadDuesDefinitions = useCallback(async () => {
    if (!currentUser?.buildingId) {
      showToast('Kullanıcıya atanmış bina bulunamadı.', 'error');
      setDuesDefinitions([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchDuesDefinitions(currentUser.buildingId);
      setDuesDefinitions(data || []);
    } catch (error) {
      showToast("Aidat tanımları yüklenirken hata: " + error.message, "error");
      setDuesDefinitions([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast, currentUser]);

  const loadApartments = useCallback(async () => {
    if (!currentUser?.buildingId) return;
    try {
      const data = await fetchApartments(currentUser.buildingId);
      setApartments(data || []);
    } catch (error) {
      showToast('Daireler yüklenirken hata: ' + error.message, 'error');
    }
  }, [showToast, currentUser]);

  useEffect(() => {
    loadDuesDefinitions();
    loadApartments();
  }, [loadDuesDefinitions, loadApartments]);

  const handleAddOrUpdateDuesDefinition = async (e) => {
    e.preventDefault();
    const name = e.target.name.value;
    const type = e.target.type.value;
    const unitPricePerShare = type === 'arsaPayi' || type === 'sayacBazli' ? parseFloat(e.target.unitPricePerShare.value) || null : null;
    const fixedAmount = type === 'sabitTutar' ? parseFloat(e.target.fixedAmount.value) || null : null;
    const responsibleParty = e.target.responsibleParty.value;
    const period = e.target.period.value;
    const totalAmountValue = parseFloat(totalAmount) || null;
    const exemptApartmentIdsJson = JSON.stringify(exemptApartmentIds);

    if (!name.trim() || !period || !responsibleParty) {
      showToast('Tanım Adı, Dönem ve Sorumlu Taraf zorunludur.', 'error');
      return;
    }
    if (type === 'arsaPayi' && !totalAmountValue && (!unitPricePerShare || unitPricePerShare <= 0)) {
      showToast('Arsa payı için birim fiyat veya toplam tutar girin.', 'error');
      return;
    }
    if (type === 'sabitTutar' && (!fixedAmount || fixedAmount <= 0)) {
      showToast('Sabit tutar için geçerli bir tutar girin.', 'error');
      return;
    }
    if (type === 'sayacBazli' && (!unitPricePerShare || unitPricePerShare <= 0)) {
      showToast('Sayaç bazlı için birim fiyat girin.', 'error');
      return;
    }

    const method = editingDuesDefinition ? 'PUT' : 'POST';
    const url = editingDuesDefinition
      ? `${process.env.REACT_APP_API_BASE_URL}/api/dues-definitions/${editingDuesDefinition.id}`
      : `${process.env.REACT_APP_API_BASE_URL}/api/dues-definitions`;

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          type,
          unitPricePerShare,
          fixedAmount,
          responsibleParty,
          period,
          totalAmount: totalAmountValue,
          distributionMethod,
          exemptApartmentIds: exemptApartmentIdsJson
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Aidat tanımı eklenemedi/güncellenemedi.');
      }
      const updatedDuesDefinition = await response.json();
      if (editingDuesDefinition) {
        setDuesDefinitions(prev => prev.map(d => d.id === editingDuesDefinition.id ? updatedDuesDefinition : d));
        showToast('Aidat tanımı başarıyla güncellendi.', 'success');
      } else {
        setDuesDefinitions(prev => [...prev, updatedDuesDefinition]);
        showToast('Aidat tanımı başarıyla eklendi.', 'success');
      }
      setIsModalOpen(false);
      setEditingDuesDefinition(null);
      setTotalAmount('');
      setDistributionMethod('arsaPayi');
      setExemptApartmentIds([]);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleDeleteDuesDefinition = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dues-definitions/${duesDefinitionToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Aidat tanımı silinemedi.');
      }
      setDuesDefinitions(prev => prev.filter(d => d.id !== duesDefinitionToDelete.id));
      showToast('Aidat tanımı başarıyla silindi.', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsConfirmationOpen(false);
      setDuesDefinitionToDelete(null);
    }
  };

  const openModal = (duesDefinition = null) => {
    setEditingDuesDefinition(duesDefinition);
    setTotalAmount(duesDefinition?.totalAmount ? duesDefinition.totalAmount.toString() : '');
    setDistributionMethod(duesDefinition?.distributionMethod || 'arsaPayi');
    setExemptApartmentIds(duesDefinition?.exemptApartmentIds ? JSON.parse(duesDefinition.exemptApartmentIds) : []);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Aidat tanımları yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Ortak Gider Tanımları</h1>
        <button
          onClick={() => openModal()}
          className="bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 transition-colors"
        >
          Yeni Ortak Gider Tanımı Ekle
        </button>
      </div>
      {duesDefinitions.length === 0 ? (
        <p className="text-center text-gray-500 py-10">Kayıtlı ortak gider tanımı bulunamadı. Yeni bir tanım ekleyin.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tanım Adı</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tür</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Birim Fiyat / Sabit Tutar</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Toplam Tutar</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sorumlu Taraf</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dönem</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oluşturma Tarihi</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Güncelleme Tarihi</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {duesDefinitions.map(def => (
                <tr key={def.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{def.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {def.type === 'arsaPayi' ? 'Arsa Payı' : def.type === 'sabitTutar' ? 'Sabit Tutar' : 'Sayaç Bazlı'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {def.type === 'arsaPayi' ? (def.unitPricePerShare ? `${def.unitPricePerShare} TL/Arsa Payı` : '-') :
                     def.type === 'sabitTutar' ? (def.fixedAmount ? `${def.fixedAmount} TL` : '-') :
                     def.unitPricePerShare ? `${def.unitPricePerShare} TL/Birim` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{def.totalAmount ? `${def.totalAmount} TL` : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {def.responsibleParty === 'ownerOnly' ? 'Kat Maliki' : 'Sakin (yoksa Kat Maliki)'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{def.period}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{new Date(def.createdAt).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{def.updatedAt ? new Date(def.updatedAt).toLocaleString() : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => openModal(def)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => {
                        setDuesDefinitionToDelete(def);
                        setIsConfirmationOpen(true);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingDuesDefinition(null);
          setTotalAmount('');
          setDistributionMethod('arsaPayi');
          setExemptApartmentIds([]);
        }}
        title={editingDuesDefinition ? "Ortak Gider Tanımı Düzenle" : "Yeni Ortak Gider Tanımı Ekle"}
        size="lg"
      >
        <form onSubmit={handleAddOrUpdateDuesDefinition}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">Tanım Adı</label>
              <input
                type="text"
                id="name"
                name="name"
                defaultValue={editingDuesDefinition?.name || ''}
                className="mt-1 block w-full p-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">Tür</label>
              <select
                id="type"
                name="type"
                defaultValue={editingDuesDefinition?.type || ''}
                className="mt-1 block w-full p-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              >
                <option value="" disabled>Seçiniz</option>
                <option value="arsaPayi">Arsa Payı</option>
                <option value="sabitTutar">Sabit Tutar</option>
                <option value="sayacBazli">Sayaç Bazlı</option>
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor="unitPricePerShare" className="block text-sm font-medium text-gray-700">
                {editingDuesDefinition?.type === 'sayacBazli' || (!editingDuesDefinition && document.getElementById('type')?.value === 'sayacBazli') 
                  ? 'Birim Fiyat (TL/m³ veya TL/kWh)' : 'Birim Fiyat (Arsa Payı için)'}
              </label>
              <input
                type="number"
                id="unitPricePerShare"
                name="unitPricePerShare"
                step="0.01"
                defaultValue={editingDuesDefinition?.unitPricePerShare || ''}
                className="mt-1 block w-full p-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                disabled={editingDuesDefinition?.type === 'sabitTutar' || (!editingDuesDefinition && document.getElementById('type')?.value === 'sabitTutar')}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="fixedAmount" className="block text-sm font-medium text-gray-700">Sabit Tutar</label>
              <input
                type="number"
                id="fixedAmount"
                name="fixedAmount"
                step="0.01"
                defaultValue={editingDuesDefinition?.fixedAmount || ''}
                className="mt-1 block w-full p-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                disabled={editingDuesDefinition?.type !== 'sabitTutar' && (!editingDuesDefinition && document.getElementById('type')?.value !== 'sabitTutar')}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="totalAmount" className="block text-sm font-medium text-gray-700">Toplam Tutar (TL, opsiyonel)</label>
              <input
                type="number"
                id="totalAmount"
                name="totalAmount"
                step="0.01"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                className="mt-1 block w-full p-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="distributionMethod" className="block text-sm font-medium text-gray-700">Dağıtım Yöntemi</label>
              <select
                id="distributionMethod"
                name="distributionMethod"
                value={distributionMethod}
                onChange={(e) => setDistributionMethod(e.target.value)}
                className="mt-1 block w-full p-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              >
                <option value="arsaPayi">Arsa Payına Göre</option>
                <option value="sabitTutar">Sabit Tutar</option>
                <option value="sayacBazli">Sayaç Bazlı</option>
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor="responsibleParty" className="block text-sm font-medium text-gray-700">Sorumlu Taraf</label>
              <select
                id="responsibleParty"
                name="responsibleParty"
                defaultValue={editingDuesDefinition?.responsibleParty || ''}
                className="mt-1 block w-full p-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              >
                <option value="" disabled>Seçiniz</option>
                <option value="ownerOnly">Kat Maliki</option>
                <option value="residentOrDefaultOwner">Sakin (Yoksa Kat Maliki)</option>
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor="period" className="block text-sm font-medium text-gray-700">Dönem (YYYY-MM)</label>
              <input
                type="month"
                id="period"
                name="period"
                defaultValue={editingDuesDefinition?.period || ''}
                className="mt-1 block w-full p-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>
            <div className="mb-4 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Muaf Daireler (Opsiyonel)</label>
              <div className="max-h-40 overflow-y-auto border p-2 rounded">
                {apartments.map(apt => (
                  <label key={apt.id} className="block text-sm">
                    <input
                      type="checkbox"
                      checked={exemptApartmentIds.includes(apt.id)}
                      onChange={() => {
                        setExemptApartmentIds(prev =>
                          prev.includes(apt.id) ? prev.filter(id => id !== apt.id) : [...prev, apt.id]
                        );
                      }}
                    />
                    <span className="ml-2">{apt.apartmentNumber}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setEditingDuesDefinition(null);
                setTotalAmount('');
                setDistributionMethod('arsaPayi');
                setExemptApartmentIds([]);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              İptal
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-sky-600 rounded-md hover:bg-sky-700"
            >
              {editingDuesDefinition ? 'Güncelle' : 'Ekle'}
            </button>
          </div>
        </form>
      </Modal>
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => {
          setIsConfirmationOpen(false);
          setDuesDefinitionToDelete(null);
        }}
        title="Ortak Gider Tanımı Silme Onayı"
        message={`"${duesDefinitionToDelete?.name}" adlı ortak gider tanımını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        onConfirm={handleDeleteDuesDefinition}
      />
    </div>
  );
};

export default CommonExpensesView;
